// ============================================================================
// REQUEST TYPES MANAGEMENT PAGE - Digital Approval System
// ============================================================================
// Admin page for managing request types with file upload configuration
// Following UI standards from SampleMasterScreen
// ============================================================================

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  useRequestTypes,
  useCreateRequestType,
  useUpdateRequestType,
  useDeleteRequestType,
} from 'hooks/api/useRequestTypes';
import type {
  RequestTypeEntity,
  CreateRequestTypeRequest,
  UpdateRequestTypeRequest,
  RequestTypeQueryParameters,
  ID,
} from 'api/types';
import RequestTypeFormModal from './RequestTypeFormModal';
import { SkeletonStyles, TableSkeleton, GridViewSkeleton } from '../DashboardSkeleton';
import { useAuth } from 'hooks/api';
import { hasAction } from 'config/rolePermissions';

// ============================================================================
// TYPES
// ============================================================================

type ModalMode = 'create' | 'edit' | 'view' | null;
type ViewMode = 'list' | 'grid';

interface RequestTypesManagementPageProps {
  className?: string;
}

// ============================================================================
// COMMON EXTENSIONS FOR SELECTION
// ============================================================================

const COMMON_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.zip',
  '.rar',
];

// ============================================================================
// ACTION MENU COMPONENT
// ============================================================================

interface ActionMenuProps {
  rowId: ID;
  canEdit: boolean;
  canDelete: boolean;
  onView: (id: ID) => void;
  onEdit: (id: ID) => void;
  onDelete: (id: ID) => void;
  onMenuStateChange: (rowId: ID, isOpen: boolean) => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  rowId,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
  onMenuStateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    onMenuStateChange(rowId, isOpen);
  }, [isOpen, rowId, onMenuStateChange]);

  return (
    <div className="action-menu-container" ref={menuRef}>
      <button
        className={`action-menu-trigger ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <IconSystem.moreVertical size={16} />
      </button>

      {isOpen && (
        <div className="action-menu-dropdown">
          <button
            className="action-menu-item"
            onClick={() => {
              onView(rowId);
              setIsOpen(false);
            }}
          >
            <IconSystem.eye size={18} />
            <span>View Details</span>
          </button>
          {canEdit && (
            <button
              className="action-menu-item"
              onClick={() => {
                onEdit(rowId);
                setIsOpen(false);
              }}
            >
              <IconSystem.edit size={18} />
              <span>Edit</span>
            </button>
          )}
          {canDelete && (
            <>
              <div className="action-menu-divider" />
              <button
                className="action-menu-item action-menu-item-danger"
                onClick={() => {
                  onDelete(rowId);
                  setIsOpen(false);
                }}
              >
                <IconSystem.trash size={18} />
                <span>Delete</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// GRID VIEW CARD COMPONENT
// ============================================================================

interface RequestTypeCardProps {
  requestType: RequestTypeEntity;
  canEdit: boolean;
  canDelete: boolean;
  onView: (id: ID) => void;
  onEdit: (id: ID) => void;
  onDelete: (id: ID) => void;
}

const RequestTypeCard: React.FC<RequestTypeCardProps> = ({
  requestType,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: '0.25rem',
              fontFamily: 'monospace',
              backgroundColor: '#f3f4f6',
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
          >
            {requestType.requestTypeCode}
          </div>
          <h3
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0,
              marginTop: '0.5rem',
            }}
          >
            {requestType.requestTypeName}
          </h3>
        </div>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            className={`action-menu-trigger ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ marginTop: '-0.25rem' }}
          >
            <IconSystem.moreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="action-menu-dropdown" style={{ right: 0, top: 'calc(100% + 0.5rem)' }}>
              <button
                className="action-menu-item"
                onClick={() => {
                  onView(requestType.id);
                  setMenuOpen(false);
                }}
              >
                <IconSystem.eye size={18} />
                <span>View Details</span>
              </button>
              {canEdit && (
                <button
                  className="action-menu-item"
                  onClick={() => {
                    onEdit(requestType.id);
                    setMenuOpen(false);
                  }}
                >
                  <IconSystem.edit size={18} />
                  <span>Edit</span>
                </button>
              )}
              {canDelete && (
                <>
                  <div className="action-menu-divider" />
                  <button
                    className="action-menu-item action-menu-item-danger"
                    onClick={() => {
                      onDelete(requestType.id);
                      setMenuOpen(false);
                    }}
                  >
                    <IconSystem.trash size={18} />
                    <span>Delete</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {requestType.description && (
        <p
          style={{
            fontSize: '0.85rem',
            color: 'var(--text-secondary)',
            margin: 0,
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {requestType.description}
        </p>
      )}

      {/* Details Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(226, 232, 240, 0.5)',
        }}
      >
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Max File Size
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
            {requestType.maxFileSizeKB ? (
              requestType.maxFileSizeKB >= 1024
                ? `${(requestType.maxFileSizeKB / 1024).toFixed(1)} MB`
                : `${requestType.maxFileSizeKB} KB`
            ) : (
              <span style={{ color: '#6b7280' }}>
                100 KB <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>(default)</span>
              </span>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Max Total Size
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
            {requestType.maxTotalSizeKB ? (
              requestType.maxTotalSizeKB >= 1024
                ? `${(requestType.maxTotalSizeKB / 1024).toFixed(1)} MB`
                : `${requestType.maxTotalSizeKB} KB`
            ) : (
              <span style={{ color: '#6b7280' }}>
                1,024 KB <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>(default)</span>
              </span>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Extensions
          </div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
            {requestType.allowedExtensions && requestType.allowedExtensions.length > 0 ? (
              `${requestType.allowedExtensions.length} types`
            ) : (
              <span style={{ color: '#6b7280' }}>
                All types <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>(default)</span>
              </span>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
            Status
          </div>
          <span className={`badge badge-${requestType.isActive ? 'active' : 'inactive'}`}>
            {requestType.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DELETE CONFIRMATION MODAL
// ============================================================================

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  loading?: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onConfirm,
  onClose,
  loading = false,
}) => {
  if (!isOpen) return null;

  // Render via portal to ensure proper z-index stacking
  return createPortal(
    <>
      {/* Backdrop - Does NOT close on click for delete confirmation */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 10060,
        }}
      />
      {/* Centered Modal Dialog */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          zIndex: 10061,
          width: '90%',
          maxWidth: '440px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1.25rem 1.5rem',
            background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
            borderBottom: '1px solid #f87171',
          }}
        >
          <IconSystem.warning size={24} style={{ color: '#dc2626' }} />
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#991b1b' }}>
            Confirm Delete
          </h3>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          <p style={{ margin: '0 0 1rem 0', color: '#475569', fontSize: '1rem' }}>
            Are you sure you want to delete this request type?
          </p>
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '0.5rem',
              padding: '1rem',
              marginBottom: '1rem',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}>
              <strong>Warning:</strong> This action cannot be undone. Any requests associated with this type may be affected.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            padding: '1rem 1.5rem',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              color: '#475569',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              background: loading ? '#fca5a5' : '#dc2626',
              border: 'none',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {loading ? (
              <>
                <IconSystem.refresh size={16} className="spinner" />
                Deleting...
              </>
            ) : (
              <>
                <IconSystem.trash size={16} />
                Delete
              </>
            )}
          </button>
        </div>
      </div>

      {/* Spinner animation styles */}
      <style>{`
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>,
    document.body
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RequestTypesManagementPage: React.FC<RequestTypesManagementPageProps> = ({
  className = '',
}) => {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [requiresAttachmentFilter, setRequiresAttachmentFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [requiresBudgetFilter, setRequiresBudgetFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [requiresValueFilter, setRequiresValueFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [selectedRows, setSelectedRows] = useState<ID[]>([]);
  const [activeMenuRowId, setActiveMenuRowId] = useState<ID | null>(null);

  const [queryParams, setQueryParams] = useState<RequestTypeQueryParameters>({
    page: 1,
    pageSize: 10,
    searchTerm: '',
    isActive: undefined,
  });
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestTypeEntity | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<ID | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [alertModal, setAlertModal] = useState<{
    title: string;
    message: string;
  } | null>(null);

  // ---------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------
  const { items, pagination, loading, error, refetch } = useRequestTypes(queryParams);

  // Configuration-driven action gates for the Request Types module.
  // `add`, `edit`, `delete` on `request-types-management` control which
  // controls render. `view` is implicit — module visibility IS the view
  // permission (opening the menu item lands on this screen).
  const { user: authUser } = useAuth();
  const canAdd = useMemo(
    () => hasAction('request-types-management', 'add', authUser?.rolePermissions),
    [authUser]
  );
  const canEditAction = useMemo(
    () => hasAction('request-types-management', 'edit', authUser?.rolePermissions),
    [authUser]
  );
  const canDeleteAction = useMemo(
    () => hasAction('request-types-management', 'delete', authUser?.rolePermissions),
    [authUser]
  );
  const { createRequestType, loading: creating } = useCreateRequestType();
  const { updateRequestType, loading: updating } = useUpdateRequestType();
  const { deleteRequestType, loading: deleting } = useDeleteRequestType();

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  const handleMenuStateChange = useCallback((rowId: ID, isOpen: boolean) => {
    setActiveMenuRowId(isOpen ? rowId : null);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setQueryParams((prev) => ({
      ...prev,
      searchTerm: value,
      page: 1,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      requiresAttachment: requiresAttachmentFilter === 'all' ? undefined : requiresAttachmentFilter === 'yes',
      requiresBudget: requiresBudgetFilter === 'all' ? undefined : requiresBudgetFilter === 'yes',
      requiresValue: requiresValueFilter === 'all' ? undefined : requiresValueFilter === 'yes',
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter, requiresAttachmentFilter, requiresBudgetFilter, requiresValueFilter]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('all');
    setRequiresAttachmentFilter('all');
    setRequiresBudgetFilter('all');
    setRequiresValueFilter('all');
    setQueryParams((prev) => ({
      ...prev,
      isActive: undefined,
      requiresAttachment: undefined,
      requiresBudget: undefined,
      requiresValue: undefined,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setQueryParams((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedRequestType(null);
    setModalMode('create');
  }, []);

  const handleEdit = useCallback(
    (id: ID) => {
      const rt = items.find((r) => r.id === id);
      if (rt) {
        setSelectedRequestType(rt);
        setModalMode('edit');
      }
    },
    [items]
  );

  const handleView = useCallback(
    (id: ID) => {
      const rt = items.find((r) => r.id === id);
      if (rt) {
        setSelectedRequestType(rt);
        setModalMode('view');
      }
    },
    [items]
  );

  const handleCloseModal = useCallback(() => {
    setModalMode(null);
    setSelectedRequestType(null);
  }, []);

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    if (type === 'error') {
      setAlertModal({ title: 'Error', message });
    } else {
      setNotification({ type, message });
      setTimeout(() => setNotification(null), 5000);
    }
  }, []);

  const handleSave = useCallback(
    async (data: CreateRequestTypeRequest | UpdateRequestTypeRequest) => {
      try {
        if (modalMode === 'create') {
          await createRequestType(data as CreateRequestTypeRequest, {
            onSuccess: () => {
              showNotification('success', 'Request type created successfully');
              handleCloseModal();
              refetch();
            },
            onError: (err) => {
              showNotification('error', err.message || 'Failed to create request type');
            },
          });
        } else if (modalMode === 'edit' && selectedRequestType) {
          await updateRequestType(selectedRequestType.id, data as UpdateRequestTypeRequest, {
            onSuccess: () => {
              showNotification('success', 'Request type updated successfully');
              handleCloseModal();
              refetch();
            },
            onError: (err) => {
              showNotification('error', err.message || 'Failed to update request type');
            },
          });
        }
      } catch (err) {
        // Error handled in options
      }
    },
    [
      modalMode,
      selectedRequestType,
      createRequestType,
      updateRequestType,
      showNotification,
      handleCloseModal,
      refetch,
    ]
  );

  const handleDelete = useCallback(
    async (id: ID) => {
      try {
        await deleteRequestType(id, {
          onSuccess: () => {
            showNotification('success', 'Request type deleted successfully');
            setDeleteConfirmId(null);
            refetch();
          },
          onError: (err) => {
            showNotification('error', err.message || 'Failed to delete request type');
          },
        });
      } catch (err) {
        // Error handled in options
      }
    },
    [deleteRequestType, showNotification, refetch]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedRows.length === items.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(items.map((r) => r.id));
    }
  }, [selectedRows.length, items]);

  const handleSelectRow = useCallback((id: ID) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  // ---------------------------------------------------------------------------
  // COMPUTED
  // ---------------------------------------------------------------------------

  const filteredItems = useMemo(() => items, [items]);

  // View mode options
  const viewModeOptions: ViewModeOption[] = [
    {
      id: 'list',
      icon: <IconSystem.list size={16} />,
      label: 'List View',
      tooltip: 'List View',
    },
    {
      id: 'grid',
      icon: <IconSystem.grid size={16} />,
      label: 'Grid View',
      tooltip: 'Grid View',
    },
  ];

  // Action buttons
  const actionButtons: ActionButton[] = [
    {
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add Request Type',
      onClick: handleCreate,
      tooltip: 'Add New Request Type',
      variant: 'icon',
      // Gated by the `add` action on request-types-management. Hidden
      // entirely when the user's role lacks it.
      visible: canAdd,
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
  ];

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div className={`module-master-container ${className}`}>
      {/* Module Header */}
      <ModuleHeader
        visible={true}
        title="Request Types Management"
        description="Configure request types and their file upload limits"
        searchConfig={{
          value: searchQuery,
          onChange: handleSearch,
          placeholder: 'Search request types...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        actions={actionButtons}
      />

      {/* Success Notification Banner */}
      {notification && (
        <div
          className="notification notification-success"
          style={{
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#d4edda',
            color: '#155724',
          }}
        >
          {notification.message}
          <button
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 0 0 16px',
              color: 'inherit',
            }}
            onClick={() => setNotification(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          style={{
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Error loading request types: {error.message}
          <button className="btn btn-secondary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Module Content */}
      <div className="module-content" style={{ marginTop: '12px' }}>
        {/* Data Grid Container */}
        <div className="data-grid-container">
          {/* Loading State - Skeleton loaders matching dashboard grid style */}
          {loading && (
            <>
              <SkeletonStyles />
              {viewMode === 'list' ? (
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>&nbsp;</th>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Max File Size</th>
                        <th>Max Total Size</th>
                        <th>Extensions</th>
                        <th>Status</th>
                        <th className="action-column" style={{ width: 80 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <TableSkeleton rowCount={8} columnCount={8} />
                    </tbody>
                  </table>
                </div>
              ) : (
                <GridViewSkeleton cardCount={6} />
              )}
            </>
          )}

          {/* LIST VIEW */}
          {!loading && !error && viewMode === 'list' && (
            <>
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>
                        <input
                          type="checkbox"
                          className="row-select-all"
                          checked={selectedRows.length === filteredItems.length && filteredItems.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Max File Size</th>
                      <th>Max Total Size</th>
                      <th>Extensions</th>
                      <th>Status</th>
                      <th className="action-column" style={{ width: 80 }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
                        >
                          No request types found
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((rt) => (
                        <tr
                          key={rt.id as string}
                          className={activeMenuRowId === rt.id ? 'has-active-menu' : ''}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="row-select"
                              checked={selectedRows.includes(rt.id)}
                              onChange={() => handleSelectRow(rt.id)}
                            />
                          </td>
                          <td>
                            <code
                              style={{
                                backgroundColor: '#f3f4f6',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontFamily: 'monospace',
                              }}
                            >
                              {rt.requestTypeCode}
                            </code>
                          </td>
                          <td>{rt.requestTypeName}</td>
                          <td>
                            {rt.maxFileSizeKB ? (
                              `${rt.maxFileSizeKB >= 1024 ? `${(rt.maxFileSizeKB / 1024).toFixed(1)} MB` : `${rt.maxFileSizeKB} KB`}`
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                100 KB <span style={{ opacity: 0.7 }}>(default)</span>
                              </span>
                            )}
                          </td>
                          <td>
                            {rt.maxTotalSizeKB ? (
                              `${rt.maxTotalSizeKB >= 1024 ? `${(rt.maxTotalSizeKB / 1024).toFixed(1)} MB` : `${rt.maxTotalSizeKB} KB`}`
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                1,024 KB <span style={{ opacity: 0.7 }}>(default)</span>
                              </span>
                            )}
                          </td>
                          <td>
                            {rt.allowedExtensions && rt.allowedExtensions.length > 0 ? (
                              <span style={{ fontSize: '12px', color: '#374151' }}>
                                {rt.allowedExtensions.slice(0, 3).join(', ')}
                                {rt.allowedExtensions.length > 3 &&
                                  ` +${rt.allowedExtensions.length - 3} more`}
                              </span>
                            ) : (
                              <span style={{ color: '#6b7280', fontSize: '12px' }}>
                                All types <span style={{ opacity: 0.7 }}>(default)</span>
                              </span>
                            )}
                          </td>
                          <td>
                            <span className={`badge badge-${rt.isActive ? 'active' : 'inactive'}`}>
                              {rt.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="action-column">
                            <ActionMenu
                              rowId={rt.id}
                              canEdit={canEditAction}
                              canDelete={canDeleteAction}
                              onView={handleView}
                              onEdit={handleEdit}
                              onDelete={(id) => setDeleteConfirmId(id)}
                              onMenuStateChange={handleMenuStateChange}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && (
                <div className="pagination-footer">
                  <div className="pagination-info">
                    <span>
                      Showing{' '}
                      {pagination.totalItems === 0
                        ? 0
                        : (pagination.currentPage - 1) * pagination.pageSize + 1}
                      -{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)}{' '}
                      of {pagination.totalItems} request types
                    </span>
                    <select
                      value={queryParams.pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{
                        marginLeft: '1rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      disabled={!pagination.hasPreviousPage}
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                    >
                      <IconSystem.chevronLeft size={16} />
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter((page) => (
                        page === 1 ||
                        page === pagination.totalPages ||
                        Math.abs(page - pagination.currentPage) <= 1
                      ))
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>...</span>
                            )}
                            <button
                              className={`page-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    <button
                      className="page-btn"
                      disabled={!pagination.hasNextPage}
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                    >
                      <IconSystem.chevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* GRID VIEW */}
          {!loading && !error && viewMode === 'grid' && (
            <>
              <div
                style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                  overflowY: 'auto',
                }}
              >
                {filteredItems.length === 0 ? (
                  <div
                    style={{
                      gridColumn: '1 / -1',
                      textAlign: 'center',
                      padding: '3rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                    <p>No request types found</p>
                  </div>
                ) : (
                  filteredItems.map((rt) => (
                    <RequestTypeCard
                      key={rt.id as string}
                      requestType={rt}
                      canEdit={canEditAction}
                      canDelete={canDeleteAction}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={(id) => setDeleteConfirmId(id)}
                    />
                  ))
                )}
              </div>

              {/* Grid View Pagination */}
              {pagination && (
                <div className="pagination-footer">
                  <div className="pagination-info">
                    <span>
                      Showing{' '}
                      {pagination.totalItems === 0
                        ? 0
                        : (pagination.currentPage - 1) * pagination.pageSize + 1}
                      -{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)}{' '}
                      of {pagination.totalItems} request types
                    </span>
                    <select
                      value={queryParams.pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      style={{
                        marginLeft: '1rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--border-light)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      disabled={!pagination.hasPreviousPage}
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                    >
                      <IconSystem.chevronLeft size={16} />
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter((page) => (
                        page === 1 ||
                        page === pagination.totalPages ||
                        Math.abs(page - pagination.currentPage) <= 1
                      ))
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsis && (
                              <span style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>...</span>
                            )}
                            <button
                              className={`page-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    <button
                      className="page-btn"
                      disabled={!pagination.hasNextPage}
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                    >
                      <IconSystem.chevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
          </div>

          <div className="search-panel-content">
            {/* Status Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Status</label>
              <div className="form-group">
                {(['all', 'active', 'inactive'] as const).map((status) => (
                  <label key={status} className="checkbox-label">
                    <input
                      type="radio"
                      name="status"
                      checked={statusFilter === status}
                      onChange={() => setStatusFilter(status)}
                    />
                    {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Requires Attachment Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Requires Attachment</label>
              <div className="form-group">
                {(['all', 'yes', 'no'] as const).map((value) => (
                  <label key={value} className="checkbox-label">
                    <input
                      type="radio"
                      name="requiresAttachment"
                      checked={requiresAttachmentFilter === value}
                      onChange={() => setRequiresAttachmentFilter(value)}
                    />
                    {value === 'all' ? 'All' : value === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            </div>

            {/* Requires Budget Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Requires Budget</label>
              <div className="form-group">
                {(['all', 'yes', 'no'] as const).map((value) => (
                  <label key={value} className="checkbox-label">
                    <input
                      type="radio"
                      name="requiresBudget"
                      checked={requiresBudgetFilter === value}
                      onChange={() => setRequiresBudgetFilter(value)}
                    />
                    {value === 'all' ? 'All' : value === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            </div>

            {/* Requires Value Filter */}
            <div className="filter-section">
              <label className="filter-section-title">Requires Value</label>
              <div className="form-group">
                {(['all', 'yes', 'no'] as const).map((value) => (
                  <label key={value} className="checkbox-label">
                    <input
                      type="radio"
                      name="requiresValue"
                      checked={requiresValueFilter === value}
                      onChange={() => setRequiresValueFilter(value)}
                    />
                    {value === 'all' ? 'All' : value === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteConfirmId}
        onConfirm={() => deleteConfirmId && handleDelete(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        loading={deleting}
      />

      {/* Error Alert Modal */}
      {alertModal && createPortal(
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10060,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 10061,
              width: '90%',
              maxWidth: '450px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1.25rem 1.5rem',
                background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                borderBottom: '1px solid #ef4444',
              }}
            >
              <IconSystem.warning size={24} style={{ color: '#dc2626' }} />
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#991b1b' }}>
                {alertModal.title}
              </h3>
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.9375rem', lineHeight: 1.6 }}>
                {alertModal.message}
              </p>
            </div>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '1rem 1.5rem',
                background: '#f8fafc',
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <button
                type="button"
                onClick={() => setAlertModal(null)}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  background: '#2563eb',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Form Modal */}
      {modalMode && (
        <RequestTypeFormModal
          mode={modalMode}
          requestType={selectedRequestType}
          commonExtensions={COMMON_EXTENSIONS}
          onClose={handleCloseModal}
          onSave={handleSave}
          loading={creating || updating}
        />
      )}
    </div>
  );
};

export default RequestTypesManagementPage;
